// BroadcastingChannelConfig.tsx — bloc configuration seul

import React, { useState, useEffect, useRef, useMemo } from 'react';
import classNames from 'classnames';
import styles from './BroadcastingChannelConfig.module.scss';

import Field            from '../../widget/inputs/Field';
import FieldColor       from '../../widget/inputs/FieldColor';
import FieldMedia       from '../../widget/inputs/FieldMedia';
import FieldMediaPendingImage from '../../widget/inputs/FieldMediaPendingImage';
import FieldSound       from '../../widget/inputs/FieldSound';
import TextArea         from '../../widget/inputs/TextArea';
import Select           from '../../widget/inputs/Select';
import Toggle           from '../../widget/inputs/Toggle';
import Icon             from '../../widget/images/Icon';
import CollapsiblePanel from '../../widget/windows/CollapsiblePanel';
import Window           from '../../widget/windows/window';
import Box              from '../../widget/windows/Box';
import TagInput         from '../../widget/dataDisplay/tags/TagInput';

import BroadcastingChannelCategoryManager from '../broadcastingChannelCategoryManagment/BroadcastingChannelCategoryManager';
import SendChannelUrlModal                from './SendChannelUrlModal';
import LiveAnnounceSection                from './LiveAnnounceSection';

import { useTypedSelector }              from '../../../store/useTypeSelector';
import { RootState }                     from '../../../store/generalReducer';
import { UploadStatus }                  from '../../../store/fileManagment/types';

import { Keyword }           from '../../../dataTypes/keywordManagment/types';
import { Product }           from '../../../dataTypes/productManagment/types';
import {
  EnrichedBroadcastingChannel,
  initializedEnrichedBroadcastingChannel,
  BroadcastingChannelCategory,
} from '../../../dataTypes/broadcastingManagment/types';
import { UserStatus } from '../../../dataTypes/userManagment/types';
import { CHAT_MODERATION_LEVELS } from '../../../dataTypes/moderationManagment/chat_types';

import {
  getThumbnailImageUrl, getPendingImageUrl, getPendingSoundUrl,
  getIngressUrlRtmp, getIngressUrlRtmps, getIngressUrlKey, getWatchingUrlRtsp, getWatchingUrlIpTv,
  getPublicWatchingUrl,
  checkExistenceBroadcastingChannel,
  getBroadcastingChannelMireFonts,
} from '../../../backendServices/broadcastingManagment/api_broadcastingChannel';
import { regenerateRtmpCredentials } from '../../../backendServices/broadcastingManagment/api_broadcastingChannel';
import { getBroadcastingChannelCategories }  from '../../../backendServices/broadcastingManagment/api_category';
import { getKeywords, createKeyword }        from '../../../backendServices/keywordManagment/api';
import { getUserStatusList }                 from '../../../backendServices/userManagment/api';
import { getProducts }                       from '../../../backendServices/productManagment/api_product';
import { generateSlug }     from '../../../utils/utils_string';

// ── Icônes ─────────────────────────────────────────────────────────────────────
const Ico = {
  Cog:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  Save: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  // Centrer horizontalement (axe x) : ligne verticale centrale + flèches vers l'extérieur
  AlignH: () => <svg viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" width="13" height="13"><line x1="12" y1="3" x2="12" y2="21"/><polyline points="7 8 3 12 7 16"/><polyline points="17 8 21 12 17 16"/></svg>,
  // Centrer verticalement (axe y) : ligne horizontale centrale + flèches vers l'extérieur
  AlignV: () => <svg viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" width="13" height="13"><line x1="3" y1="12" x2="21" y2="12"/><polyline points="8 7 12 3 16 7"/><polyline points="8 17 12 21 16 17"/></svg>,
};

// ── Mesure la largeur d'un texte pour le centrage horizontal des surimpressions ──
const measureTextWidth = (text: string, font: string, size: number): number => {
  if (!text) return 0;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 0;
  ctx.font = `${size}px ${font}`;
  return ctx.measureText(text).width;
};

// Repère du paysage ; en portrait les deux côtés sont échangés (cf. refW / refH dans le composant),
// et transmis à FieldMediaPendingImage (referenceWidth/referenceHeight) : c'est le canvas de
// référence sur lequel x/y/size des surimpressions sont exprimés.
const REFERENCE_WIDTH = 1920;
const REFERENCE_HEIGHT = 1080;

// L'image d'attente et le son d'attente sont deux réglages du même objet : leurs aperçus
// se répondent, donc ils partagent une largeur — d'où cette constante plutôt que deux
// "300px" écrits côte à côte, qui divergeraient à la première retouche.
const PENDING_PREVIEW_WIDTH = '300px';
// Même hauteur d'aperçu en portrait : 300 × 9/16.
const PENDING_PREVIEW_WIDTH_PORTRAIT = '169px';
// Aperçu de la mire à l'écran (la carte Mire d'attente est plus large que l'ancienne colonne).
const MIRE_PREVIEW_WIDTH          = '100%';   // s'adapte à la carte ; la taille des textes suit la largeur mesurée
const MIRE_PREVIEW_WIDTH_PORTRAIT = '216px';

// Curseur des réglages de la mire (taille, fond). Il ne fait que doubler le champ numérique
// voisin : même setter, même valeur, jamais une seconde source de vérité.
const RangeInput: React.FC<{ value: number; min: number; max: number; onChange: (v: number) => void; label: string }> =
  ({ value, min, max, onChange, label }) => (
    <input type="range" className={styles.range} aria-label={label}
           min={min} max={max} step={1}
           value={Math.min(max, Math.max(min, Number.isFinite(value) ? value : min))}
           onChange={e => onChange(Number(e.target.value))} />
  );

// ── Polices disponibles pour les surimpressions de la mire d'attente ───────────
// Valeur affichée le temps que getBroadcastingChannelMireFonts() réponde ; la liste réelle
// vient du microservice ffmpeg (fontconfig), seul rendu qui compte pour la mire.
const DEFAULT_MIRE_FONT_CHOICES = [
  'Arial', 'Helvetica', 'Verdana', 'Georgia', 'Times New Roman',
  'Courier New', 'Roboto', 'Open Sans', 'Impact', 'Trebuchet MS',
];

// ── Niveaux de modération du chat (même échelle que Video.commentsModeration) ──
// Les quatre niveaux du chat en direct, ceux de l'écran Modération et de l'IA de
// Google (customer-api, GoogleChatModerator) : 0 légère, 1 standard, 2 avancée,
// 3 manuelle. Plus de « modération oui/non » : le chat est TOUJOURS trié par l'IA,
// seul le degré change (2026-10-08).
const chatModerationChoices = CHAT_MODERATION_LEVELS.map(l => String(l.value));
const chatModerationLabels  = CHAT_MODERATION_LEVELS.map(l => l.label);
/** Niveau proposé quand on ouvre un chat : celui que l'écran Modération applique aussi. */
const DEFAULT_CHAT_LEVEL = '1';

// ── Fuseaux horaires disponibles pour l'incrustation de l'heure sur la mire d'attente ──
const TIMEZONE_CHOICES: string[] = (() => {
  try {
    if (typeof (Intl as any).supportedValuesOf === 'function') return (Intl as any).supportedValuesOf('timeZone');
  } catch { /* Intl.supportedValuesOf indisponible sur ce navigateur */ }
  return [
    'Europe/Paris', 'Europe/London', 'Europe/Madrid', 'Europe/Berlin', 'Europe/Rome',
    'Europe/Brussels', 'Europe/Lisbon', 'Europe/Zurich', 'Europe/Amsterdam', 'Europe/Dublin',
    'Europe/Moscow', 'Europe/Athens', 'Europe/Bucharest', 'Europe/Warsaw', 'Europe/Vienna',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Sao_Paulo', 'America/Mexico_City', 'America/Toronto', 'America/Montreal',
    'Africa/Casablanca', 'Africa/Tunis', 'Africa/Algiers', 'Africa/Dakar', 'Africa/Abidjan',
    'Asia/Dubai', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Kolkata',
    'Australia/Sydney', 'Pacific/Auckland', 'UTC',
  ];
})();

const timezoneLabel = (tz: string): string => {
  try {
    const offset = new Intl.DateTimeFormat('fr-FR', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(new Date())
      .find(p => p.type === 'timeZoneName')?.value ?? '';
    return `${tz} (${offset})`;
  } catch {
    return tz;
  }
};

const TIMEZONE_LABELS: string[] = TIMEZONE_CHOICES.map(timezoneLabel);

// ── Props ──────────────────────────────────────────────────────────────────────
export interface BroadcastingChannelConfigProps {
  broadcastingChannelToEdit?   : EnrichedBroadcastingChannel;
  onBroadcastingChannelChange? : (
    bc: EnrichedBroadcastingChannel,
    lf: File|null, pif: File|null, psf: File|null,
    lm: boolean, pim: boolean, psm: boolean,
    paid: boolean, price: number, prod: Product|null,
    cb: (ok: boolean) => void
  ) => void;
  creationMode?                : boolean;
  onCancel?                    : () => void;
  onReady?                     : () => void;
  subtitle?                    : string;
  // « Image d'attente » du tableau de bord : compteur qui change à chaque demande
  defaultPendingImage?         : string;
  // Incrémenté par le parent à chaque sauvegarde réussie : force la relecture de
  // broadcastingChannelToEdit et la réinitialisation du formulaire (nbMods → 0),
  // y compris en mode édition où l'id ne change pas (cf. useEffect plus bas).
  saveVersion?                 : number;
}

// ── Composant ─────────────────────────────────────────────────────────────────
export const BroadcastingChannelConfig = ({
  broadcastingChannelToEdit,
  onBroadcastingChannelChange,
  creationMode,
  onCancel,
  onReady,
  subtitle = "Gérez les paramètres et les préférences de votre chaîne.",
  defaultPendingImage,
  saveVersion,
}: BroadcastingChannelConfigProps) => {


  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);

  const isEditMode = !creationMode ?? true;
  let initialBC = isEditMode ? broadcastingChannelToEdit! : initializedEnrichedBroadcastingChannel();
  const hasCalledOnReady = useRef(false);

  // ── Données référentielles ─────────────────────────────────────────────────
  const [bcCategories,     setBcCategories]     = useState<BroadcastingChannelCategory[]>([]);
  const [kwDict,           setKwDict]           = useState<Keyword[]>([]);
  const [usedKw,           setUsedKw]           = useState<Keyword[]>([]);
  const [userStatuses,     setUserStatuses]     = useState<UserStatus[]>([]);
  const [payProducts,      setPayProducts]      = useState<Product[]>([]);
  const [mireFontChoices,  setMireFontChoices]  = useState<string[]>(DEFAULT_MIRE_FONT_CHOICES);

  // ── Champs du formulaire ───────────────────────────────────────────────────
  const [name,             setName]             = useState(initialBC.name);
  const [description,      setDescription]      = useState(initialBC.description);
  const [mode,             setMode]             = useState(initialBC.mode);
  // Sens du programme : choisi à la création, modifiable ensuite mais seulement chaîne ARRÊTÉE
  // (le microservice le lit au démarrage de son conteneur ; le serveur refuse sinon). Il fixe
  // aussi le repère de la mire : 1920×1080 ou 1080×1920.
  const [orientation,      setOrientation]      = useState<'landscape' | 'portrait'>(initialBC.orientation ?? 'landscape');
  const channelRunning = isEditMode && (broadcastingChannelToEdit?.channelStatus ?? 'inactive') !== 'inactive';
  const refW = orientation === 'portrait' ? REFERENCE_HEIGHT : REFERENCE_WIDTH;
  const refH = orientation === 'portrait' ? REFERENCE_WIDTH  : REFERENCE_HEIGHT;
  const [showTitle,        setShowTitle]        = useState(initialBC.showTitle);
  const [showDate,         setShowDate]         = useState(initialBC.showDate);
  const [titleFont,        setTitleFont]        = useState(initialBC.titleFont);
  const [titleColor,       setTitleColor]       = useState(initialBC.titleColor);
  const [titleX,           setTitleX]           = useState(initialBC.titleX);
  const [titleY,           setTitleY]           = useState(initialBC.titleY);
  const [titleSize,        setTitleSize]        = useState(initialBC.titleSize);
  const [titleBoxOpacity,  setTitleBoxOpacity]  = useState(initialBC.titleBoxOpacity);
  const [dateBoxOpacity,   setDateBoxOpacity]   = useState(initialBC.dateBoxOpacity);
  const [titleBold,        setTitleBold]        = useState(initialBC.titleBold);
  const [titleUnderline,   setTitleUnderline]   = useState(initialBC.titleUnderline);
  const [titleItalic,      setTitleItalic]      = useState(initialBC.titleItalic);
  const [dateFont,         setDateFont]         = useState(initialBC.dateFont);
  const [dateColor,        setDateColor]        = useState(initialBC.dateColor);
  const [dateX,            setDateX]            = useState(initialBC.dateX);
  const [dateY,            setDateY]            = useState(initialBC.dateY);
  const [dateSize,         setDateSize]         = useState(initialBC.dateSize);
  const [dateBold,         setDateBold]         = useState(initialBC.dateBold);
  const [dateUnderline,    setDateUnderline]    = useState(initialBC.dateUnderline);
  const [dateItalic,       setDateItalic]       = useState(initialBC.dateItalic);
  const [timezone,         setTimezone]         = useState(initialBC.timezone);
  // Heure figée à l'ouverture du formulaire, uniquement pour l'aperçu de la mire (pas de rafraîchissement par seconde), recalculée si le fuseau change
  const [previewTimeText, setPreviewTimeText] = useState(() => new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: timezone || undefined }));
  const [categoriesIds,    setCategoriesIds]    = useState<number[]>(initialBC.categoriesIds);
  const [keywordsIds,      setKeywordsIds]      = useState<number[]>(initialBC.keywordsIds);
  const [chatAllowed,      setChatAllowed]      = useState(initialBC.chatModeration !== -1);
  const [chatModerationLevel, setChatModerationLevel] = useState(initialBC.chatModeration >= 0 ? initialBC.chatModeration.toString() : DEFAULT_CHAT_LEVEL);
  // « Ouvrir le plateau au public » (2026-10-15) : réglage réservé aux chaînes nées dans
  // cette régie ; fermé par défaut, y compris à la création.
  const [stageOpen,        setStageOpen]        = useState(initialBC.stageOpen);
  const [,  setAssocProductIds]  = useState<number[]>(initialBC.associatedProductIds);
  const [requiredStatusId, setRequiredStatusId] = useState(initialBC.requiredUserStatusId > 0 ? initialBC.requiredUserStatusId : 0);
  const [assocProduct,     setAssocProduct]     = useState<Product|null>(null);
  const [soldPrice,        setSoldPrice]        = useState('');
  const [initialSoldPrice, setInitialSoldPrice] = useState('');

  // Statut "Paiement éphémère" : seul statut qui rend la chaîne payante et fait apparaître le champ prix.
  const PAID_STATUS_ID = 5;
  const isPaidStatus = requiredStatusId === PAID_STATUS_ID;

  // ── Médias ─────────────────────────────────────────────────────────────────
  const [logoFile,         setLogoFile]         = useState(initialBC.logoImageFile);
  const [logoSrc,          setLogoSrc]          = useState(getThumbnailImageUrl(initialBC));
  const [logoUpload,       setLogoUpload]       = useState<File|null>(null);
  const [logoDel,          setLogoDel]          = useState(false);
  const [pendImgFile,      setPendImgFile]      = useState(initialBC.pendingImageFile);
  const [pendImgSrc,       setPendImgSrc]       = useState(getPendingImageUrl(initialBC));
  const [pendImgUpload,    setPendImgUpload]    = useState<File|null>(null);
  const [pendImgDel,       setPendImgDel]       = useState(false);
  const [pendSndFile,      setPendSndFile]      = useState(initialBC.pendingSoundFile);
  const [pendSndSrc,       setPendSndSrc]       = useState(getPendingSoundUrl(initialBC));
  const [pendSndUpload,    setPendSndUpload]    = useState<File|null>(null);
  const [pendSndDel,       setPendSndDel]       = useState(false);

  // ── Envoi du son d'attente, en tâche de fond ───────────────────────────────
  // Après une sauvegarde, init() réaffiche `pendingSoundFile` tel que l'API le connaît,
  // c'est-à-dire l'ANCIEN son tant que l'upload par morceaux n'est pas terminé. Sans ce
  // témoin, l'écran donnerait à croire que la modification n'a pas été prise en compte.
  const uploadsById = useTypedSelector((state: RootState) => state.fileUploadReducer.uploads);

  // On lit l'id depuis la prop plutôt que depuis initialBC : cette variable-là est réaffectée
  // dans un effet, donc sa valeur ne survit pas au rendu suivant.
  const pendSndChannelId = broadcastingChannelToEdit?.id ?? 0;

  // Nouvelle clé RTMP : l'ancienne cesse de fonctionner pour toute NOUVELLE connexion d'OBS.
  const handleRegenerateRtmpKey = async () => {
    if (!broadcastingChannelToEdit || regenBusy) return;
    if (!window.confirm("Régénérer la clé de stream ?\n\nL'ancienne clé ne fonctionnera plus : OBS devra être reconfiguré avec la nouvelle.")) return;
    setRegenBusy(true); setRegenError('');
    const result = await regenerateRtmpCredentials(broadcastingChannelToEdit.id);
    setRegenBusy(false);
    if (result.code !== 1) { setRegenError(result.comment); return; }
    // La fiche parente garde cet objet : sans cette mise à jour, une réinitialisation du formulaire
    // réafficherait l'ANCIENNE clé, qui ne fonctionne plus.
    Object.assign(broadcastingChannelToEdit, { rtmpLogin: result.payload.rtmpLogin, rtmpPassword: result.payload.rtmpPassword });
    setStreamKey(getIngressUrlKey({ ...broadcastingChannelToEdit, rtmpLogin: result.payload.rtmpLogin, rtmpPassword: result.payload.rtmpPassword }));
  };

  const pendSndUploadState = useMemo(
    () => Object.values(uploadsById)
                .filter(upload => upload.entityType === 'broadcastingChannel' && upload.entityId === pendSndChannelId)
                .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
                [0],
    [uploadsById, pendSndChannelId]
  );

  const pendSndSending = pendSndUploadState !== undefined &&
                         [ UploadStatus.PENDING   ,
                           UploadStatus.UPLOADING ,
                           UploadStatus.RETRYING  ,
                           UploadStatus.PAUSED    ].includes(pendSndUploadState.status);

  const pendSndSendFailed = pendSndUploadState?.status === UploadStatus.FAILED;

  // ── URLs de flux ───────────────────────────────────────────────────────────
  const [rtmpUrl,           setRtmpUrl]           = useState(getIngressUrlRtmp());
  const [streamKey,         setStreamKey]         = useState(getIngressUrlKey(initialBC));
  const [regenBusy,         setRegenBusy]         = useState(false);
  const [regenError,        setRegenError]        = useState('');
  const [rtspUrl,           setRtspUrl]           = useState(getWatchingUrlRtsp(initialBC));
  const [publicUrl,         setPublicUrl]         = useState(getPublicWatchingUrl(initialBC));
  const [ipTvUrl,           setIpTvUrl]           = useState(getWatchingUrlIpTv(initialBC));
  const [ipTvGenericalUser, setIpTvGenericalUser] = useState(initialBC.ipTvGenericalUser);
  const [ipTvGenericalPwd,  setIpTvGenericalPwd]  = useState(initialBC.ipTvGenericalPwd);

  // ── UI / validation ────────────────────────────────────────────────────────
  const [nameErr,       setNameErr]       = useState('');
  const [statusErr,     setStatusErr]     = useState('');
  const [catErr,        setCatErr]        = useState('');
  const [checkErr,      setCheckErr]      = useState(false);
  // Fichier refusé parce qu'il n'est pas du type attendu par son champ. La page n'a
  // aucune zone d'erreur sous les champs : le refus passe donc par la même fenêtre
  // modale que « La fiche comporte des erreurs ».
  const [fileErr,       setFileErr]       = useState('');
  const [catWindowOpen, setCatWindowOpen] = useState(false);

  // ── Envoi URL publique par mail ────────────────────────────────────────────
  const [sendUrlModalOpen, setSendUrlModalOpen] = useState(false);

  // ── Sélection Nom / Heure sur la mire d'attente ────────────────────────────
  // Au plus un élément sélectionné à la fois : affiche son contour + son ancre de
  // redimensionnement sur l'aperçu, et sa colonne de réglages à droite. Un clic en dehors
  // de cette colonne de réglages (aperçu, reste du formulaire...) désélectionne tout —
  // un clic à l'intérieur de la colonne de réglages ne désélectionne pas, pour permettre
  // de régler l'élément sélectionné.
  const [selectedOverlay, setSelectedOverlay] = useState<'title' | 'date' | null>(null);
  const mireRightRef = useRef<HTMLDivElement>(null);

  // ── Référence du compteur de modifications ────────────────────────────────
  // nbMods compare l'état courant à `initialBC`. Pour la quasi-totalité des champs
  // c'est juste : init() y recopie `bc` tel quel, la valeur initiale du champ EST
  // celle de la chaîne. Six champs font exception — en création, init() les CALCULE
  // (nom centré à 4× sa taille, heure en bas à 2×) au lieu de reprendre les défauts
  // du type (0, 0, 64). Comparés à ces défauts, ils annonçaient six modifications
  // sur une fiche que personne n'avait touchée. La référence, c'est donc ce qu'init()
  // a posé, pas ce que le type propose.
  const overlayRef = useRef({ titleX: initialBC.titleX, titleY: initialBC.titleY, titleSize: initialBC.titleSize,
                              dateX:  initialBC.dateX,  dateY:  initialBC.dateY,  dateSize:  initialBC.dateSize });

  // Ce que la colonne de droite montre. Une sélection explicite prime — tant que
  // l'élément est encore affiché sur la mire : décocher « Nom » alors qu'il est
  // sélectionné laisserait sinon ses réglages à l'écran sans rien à régler.
  // Faute de sélection, le nom passe devant l'heure, et si aucun des deux n'est
  // activé il n'y a rien à montrer : c'est le message qui parle.
  const overlayAffiche: 'title' | 'date' | null =
      (selectedOverlay === 'title' && showTitle) ? 'title'
    : (selectedOverlay === 'date'  && showDate ) ? 'date'
    : showTitle                                  ? 'title'
    : showDate                                   ? 'date'
    :                                              null;

  useEffect(() => {
    if (!selectedOverlay) return;
    const handleOutsideMouseDown = (e: MouseEvent) => {
      if (mireRightRef.current?.contains(e.target as Node)) return;
      setSelectedOverlay(null);
    };
    document.addEventListener('mousedown', handleOutsideMouseDown);
    return () => document.removeEventListener('mousedown', handleOutsideMouseDown);
  }, [selectedOverlay]);

  // ── init ────────────────────────────────────────────────────────────────────
  // Un logo, une mire ou un son remplacé garde la MÊME URL (…/images/thumbnail.webp) : le serveur de
  // médias répond bien `no-cache`, mais un <img> ou <audio> dont le `src` ne change pas n'est jamais
  // relu — il fallait recharger la page pour voir le nouveau fichier (constaté le 20/09/2026).
  // Après une sauvegarde réussie (saveVersion > 0), on suffixe donc l'URL d'un numéro de version.
  const nouvelleVersion = (url: string): string =>
    (saveVersion && url) ? `${url}${url.includes('?') ? '&' : '?'}v=${saveVersion}` : url;

  const init = () => {
    const bc = isEditMode ? broadcastingChannelToEdit! : initializedEnrichedBroadcastingChannel();
    setName(bc.name); setDescription(bc.description); setMode(bc.mode);
    setOrientation(bc.orientation ?? 'landscape');
    setShowTitle(bc.showTitle); setShowDate(bc.showDate);
    setTitleFont(bc.titleFont); setTitleColor(bc.titleColor);
    // Nouvelle chaîne (pas encore de position/taille enregistrée) : nom centré au milieu de la
    // mire à 4x sa taille par défaut, heure centrée en bas (avec une marge, pas collée au bord)
    // à 2x sa taille par défaut. En édition, on garde la position/taille déjà enregistrée.
    const newChannelTitleSize = bc.titleSize * 4;
    const newChannelDateSize  = bc.dateSize  * 2;
    const newChannelDateBottomMargin = 80;

    const titleXInit    = isEditMode ? bc.titleX    : Math.round((refW  - measureTextWidth(bc.name, bc.titleFont, newChannelTitleSize)) / 2);
    const titleYInit    = isEditMode ? bc.titleY    : Math.round((refH - newChannelTitleSize) / 2);
    const titleSizeInit = isEditMode ? bc.titleSize : newChannelTitleSize;

    setTitleX(titleXInit); setTitleY(titleYInit); setTitleSize(titleSizeInit);
    setTitleBold(bc.titleBold); setTitleUnderline(bc.titleUnderline); setTitleItalic(bc.titleItalic);
    setTitleBoxOpacity(bc.titleBoxOpacity); setDateBoxOpacity(bc.dateBoxOpacity);
    setDateFont(bc.dateFont); setDateColor(bc.dateColor);
    const dateXInit    = isEditMode ? bc.dateX    : Math.round((refW - measureTextWidth(previewTimeText, bc.dateFont, newChannelDateSize)) / 2);
    const dateYInit    = isEditMode ? bc.dateY    : refH - newChannelDateSize - newChannelDateBottomMargin;
    const dateSizeInit = isEditMode ? bc.dateSize : newChannelDateSize;

    setDateX(dateXInit); setDateY(dateYInit); setDateSize(dateSizeInit);

    // La référence du compteur suit ce qui vient d'être posé, pas les défauts du type.
    overlayRef.current = { titleX: titleXInit, titleY: titleYInit, titleSize: titleSizeInit,
                           dateX:  dateXInit,  dateY:  dateYInit,  dateSize:  dateSizeInit };
    setDateBold(bc.dateBold); setDateUnderline(bc.dateUnderline); setDateItalic(bc.dateItalic);
    setTimezone(bc.timezone);
    setCategoriesIds(bc.categoriesIds); setKeywordsIds(bc.keywordsIds); setAssocProductIds(bc.associatedProductIds);
    setChatAllowed(bc.chatModeration !== -1);
    setChatModerationLevel(bc.chatModeration >= 0 ? bc.chatModeration.toString() : DEFAULT_CHAT_LEVEL);
    setStageOpen(bc.stageOpen);
    // À défaut de statut requis explicite (nouvelle chaîne), on présélectionne le premier choix
    // de la liste si elle est déjà chargée — cf. l'effet ci-dessous pour le cas où elle ne l'est pas encore.
    setRequiredStatusId(bc.requiredUserStatusId > 0 ? bc.requiredUserStatusId : (userStatuses[0]?.id ?? 0));
    setLogoFile(bc.logoImageFile); setLogoSrc(nouvelleVersion(getThumbnailImageUrl(bc))); setLogoUpload(null); setLogoDel(false);
    setPendImgFile(bc.pendingImageFile); setPendImgSrc(nouvelleVersion(getPendingImageUrl(bc))); setPendImgUpload(null); setPendImgDel(false);
    setPendSndFile(bc.pendingSoundFile); setPendSndSrc(nouvelleVersion(getPendingSoundUrl(bc))); setPendSndUpload(null); setPendSndDel(false);
    setRtmpUrl(getIngressUrlRtmp()); setStreamKey(getIngressUrlKey(bc));
    setRtspUrl(getWatchingUrlRtsp(bc)); setPublicUrl(getPublicWatchingUrl(bc));
    setIpTvUrl(getWatchingUrlIpTv(bc));
    setIpTvGenericalUser(bc.ipTvGenericalUser);
    setIpTvGenericalPwd(bc.ipTvGenericalPwd);
    setNameErr(''); setStatusErr(''); setCatErr('');
    setCheckErr(false);
    setSendUrlModalOpen(false);
  };

  // saveVersion : rejoue init() après toute sauvegarde réussie, y compris en mode édition où
  // broadcastingChannelToEdit?.id (dépendance de l'effet plus bas) ne change pas — sans quoi
  // nbMods restait > 0 (bouton rouge) après un enregistrement qui avait pourtant réussi.
  useEffect(() => { init(); }, [creationMode, saveVersion]);

  // Fiche modifiée AILLEURS (appli mobile, autre poste) pendant qu'elle est ouverte : le formulaire
  // suit le serveur tant que l'opérateur n'y a rien changé. Dès qu'une modification est en cours
  // (nbMods > 0) on ne touche à rien : la remplacer ferait perdre sa saisie.
  const remoteSignature = (isEditMode && broadcastingChannelToEdit)
    ? JSON.stringify([ broadcastingChannelToEdit.name, broadcastingChannelToEdit.description,
                       broadcastingChannelToEdit.categoriesIds, broadcastingChannelToEdit.keywordsIds,
                       broadcastingChannelToEdit.requiredUserStatusId, broadcastingChannelToEdit.chatModeration,
                       broadcastingChannelToEdit.stageOpen ])
    : '';
  const nbModsRef             = useRef(0);
  // nbMods du rendu PRÉCÉDENT. Celui du rendu courant (nbModsRef) est calculé contre la
  // fiche déjà mise à jour, alors que le formulaire porte encore l'ancienne valeur : le
  // changement distant lui-même y compte comme une saisie, et la fiche ne suivait jamais le
  // serveur (plateau fermé depuis la régie, Configuration restée sur « ouvert »).
  const nbModsBeforeRef        = useRef(0);
  const lastRemoteSignatureRef = useRef(remoteSignature);
  useEffect(() => {
    if (remoteSignature === lastRemoteSignatureRef.current) return;
    lastRemoteSignatureRef.current = remoteSignature;
    if (nbModsBeforeRef.current === 0) init();
  }, [remoteSignature]);

  useEffect(() => {
    try {
      setPreviewTimeText(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: timezone || undefined }));
    } catch {
      setPreviewTimeText(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    }
  }, [timezone]);

  useEffect(() => {
    if (broadcastingChannelToEdit) { initialBC = broadcastingChannelToEdit; init(); }
    if (!creationMode && !(broadcastingChannelToEdit?.id)) return;
    const load = async () => {
      try {
        const [cr, kr, sr, pr] = await Promise.all([
          getBroadcastingChannelCategories(authentication.admin?.id), getKeywords(), getUserStatusList(), getProducts('Boutique', authentication.admin?.id),
        ]);
        if (cr.code === 1) setBcCategories(cr.payload.sort((a,b) => a.rank-b.rank));
        if (kr.code === 1) { setKwDict(kr.payload); if (broadcastingChannelToEdit) setUsedKw(kr.payload.filter(k => broadcastingChannelToEdit.keywordsIds.includes(k.id))); }
        if (sr.code === 1) setUserStatuses(sr.payload);
        if (pr.code === 1) setPayProducts(pr.payload);
      } catch(e) { console.error(e); }
      finally {
        if (onReady && !hasCalledOnReady.current) {
          hasCalledOnReady.current = true;
          requestAnimationFrame(() => requestAnimationFrame(() => onReady!()));
        }
      }
    };
    load();
  // ⚠️ Dépendance volontairement restreinte à l'id (et non l'objet broadcastingChannelToEdit entier) :
  // ce dernier est recréé (nouvelle référence, mêmes valeurs) par le polling de statut du Sheet parent
  // toutes les 4s pendant activating/deactivating. Dépendre de l'objet complet réexécutait init() à
  // chaque tick et écrasait silencieusement les modifications en cours (ex: gras/souligné/italique).
  }, [broadcastingChannelToEdit?.id]);

  useEffect(() => {
    if (broadcastingChannelToEdit && kwDict.length) setUsedKw(kwDict.filter(k=>broadcastingChannelToEdit.keywordsIds.includes(k.id)));
  }, [broadcastingChannelToEdit, kwDict]);

  // Polices réellement disponibles côté microservice (fontconfig) — indépendant du canal édité
  useEffect(() => {
    getBroadcastingChannelMireFonts().then(r => {
      if (r.code === 1 && r.payload.length > 0) {
        console.log('[BroadcastingChannelConfig] polices mire d\'attente reçues :', r.payload, '—', r.comment);
        setMireFontChoices(r.payload);
      } else {
        console.warn('[BroadcastingChannelConfig] échec récupération polices, conservation de la liste par défaut', r);
      }
    });
  }, []);

  // Après une sauvegarde réussie, le produit d'abonnement a pu être créé (passage en payant)
  // ou changer de prix : la liste chargée au montage est périmée. Sans relecture, assocProduct
  // restait null ou à l'ancien prix — le compteur gardait « 1 modification » (prix ≠ prix initial)
  // et le clic suivant créait un SECOND produit au lieu de modifier le premier (Alès TV, 24/10).
  useEffect(() => {
    if (!saveVersion) return;
    getProducts('Boutique', authentication.admin?.id)
      .then(pr => { if (pr.code === 1) setPayProducts(pr.payload); })
      .catch(e => console.error(e));
  }, [saveVersion]);

  // Plusieurs produits peuvent être rattachés à la chaîne (doublons créés avant le correctif
  // ci-dessus) : le plus récent fait foi, c'est celui que le dernier enregistrement a posé.
  useEffect(() => {
    const ids = broadcastingChannelToEdit?.associatedProductIds ?? [];
    const p = payProducts.filter(x => ids.includes(x.id)).sort((a, b) => b.id - a.id)[0];
    setAssocProduct(p ?? null);
  }, [payProducts, broadcastingChannelToEdit]);

  useEffect(() => {
    const price = assocProduct?.variants?.[0]?.prices?.[0]
      ? (assocProduct.variants[0].prices[0].priceWithTax).toFixed(2)
      : '';
    setSoldPrice(price);
    setInitialSoldPrice(price);
  }, [assocProduct]);

  useEffect(() => {
    if (userStatuses.length > 0 && requiredStatusId <= 0) {
      setRequiredStatusId(userStatuses[0].id);
    }
  }, [userStatuses]);

  // ── helpers ─────────────────────────────────────────────────────────────────
  const eq = (a:number[],b:number[]) => { const sa=[...a].sort(),sb=[...b].sort(); return sa.length===sb.length&&sa.every((v,i)=>v===sb[i]); };

  // -1 : non autorisé, 0 : autorisé sans modération, 1-3 : niveau de modération choisi
  // -1 : chat fermé ; sinon le niveau. admin-api ouvre ou ferme le salon d'après lui.
  const chatModerationValue = !chatAllowed ? -1 : parseInt(chatModerationLevel, 10);

  // « Monter sur le plateau » n'existe que pour une chaîne née dans la régie web : une
  // chaîne créée par les apps iPhone ou Android ne montre pas le réglage (consigne de
  // l'utilisateur du 2026-10-15). En création, la chaîne naît ici : il est proposé.
  const stageAvailable = !isEditMode || initialBC.createdFromRegie;

  // ── Nombre de modifications : nombre de champs dont la valeur actuelle diffère de sa valeur initiale
  //    (recalculé à chaque rendu — pas de compteur incrémental, donc pas de dérive si un même champ est modifié plusieurs fois) ──
  const nbMods = (() => {
    let n = 0;
    if (name !== initialBC.name) n++;
    if (description !== initialBC.description) n++;
    if (orientation !== (initialBC.orientation ?? 'landscape')) n++;
    if (showTitle !== initialBC.showTitle) n++;
    if (showDate !== initialBC.showDate) n++;
    if (titleFont !== initialBC.titleFont) n++;
    if (titleColor !== initialBC.titleColor) n++;
    if (titleX !== overlayRef.current.titleX) n++;
    if (titleY !== overlayRef.current.titleY) n++;
    if (titleSize !== overlayRef.current.titleSize) n++;
    if (titleBoxOpacity !== initialBC.titleBoxOpacity) n++;
    if (dateBoxOpacity !== initialBC.dateBoxOpacity) n++;
    if (titleBold !== initialBC.titleBold) n++;
    if (titleUnderline !== initialBC.titleUnderline) n++;
    if (titleItalic !== initialBC.titleItalic) n++;
    if (dateFont !== initialBC.dateFont) n++;
    if (dateColor !== initialBC.dateColor) n++;
    if (dateX !== overlayRef.current.dateX) n++;
    if (dateY !== overlayRef.current.dateY) n++;
    if (dateSize !== overlayRef.current.dateSize) n++;
    if (dateBold !== initialBC.dateBold) n++;
    if (dateUnderline !== initialBC.dateUnderline) n++;
    if (dateItalic !== initialBC.dateItalic) n++;
    if (timezone !== initialBC.timezone) n++;
    if (!eq(categoriesIds, initialBC.categoriesIds)) n++;
    if (!eq(keywordsIds, initialBC.keywordsIds)) n++;
    if (chatModerationValue !== initialBC.chatModeration) n++;
    if (stageOpen !== initialBC.stageOpen) n++;
    const initialStatusId = initialBC.requiredUserStatusId > 0 ? initialBC.requiredUserStatusId : (userStatuses[0]?.id ?? 0);
    if (requiredStatusId !== initialStatusId) n++;
    if (isPaidStatus && soldPrice !== initialSoldPrice) n++;
    if (ipTvGenericalUser !== initialBC.ipTvGenericalUser) n++;
    if (ipTvGenericalPwd !== initialBC.ipTvGenericalPwd) n++;
    if (logoUpload || logoDel) n++;
    if (pendImgUpload || pendImgDel) n++;
    if (pendSndUpload || pendSndDel) n++;
    return n;
  })();
  nbModsRef.current = nbMods;
  // Déclaré APRÈS l'effet de remoteSignature : il lit la valeur d'avant avant qu'on la remplace.
  useEffect(() => { nbModsBeforeRef.current = nbMods; });

  const testName = async (v:string):Promise<string> => {
    if (!v) return 'Le nom ne peut pas être vide';
    // Nom inchangé par rapport à la chaîne en cours d'édition : pas besoin d'interroger le
    // backend, qui renverrait de toute façon "existe" puisqu'il s'agit de cette même chaîne.
    if (isEditMode && v === initialBC.name) return '';
    const r = await checkExistenceBroadcastingChannel(v);
    return r.code===1 ? (r.payload ? 'Cette chaîne existe déjà' : '') : 'Erreur de vérification';
  };

  const onName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    testName(newName).then(err => {
      setNameErr(err);
      // getPublicWatchingUrl lit désormais `slug`, pas `name` : l'aperçu doit donc
      // prédire le slug qu'onSave calculera réellement (generateSlug(name)), pas
      // se contenter de changer le nom d'un objet dont le slug resterait figé.
      if (!err) setPublicUrl(getPublicWatchingUrl({ ...initialBC, slug: generateSlug(newName) }));
    });
  };
  const onDesc      = (e:React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value);
  const onShowTitle = (v:boolean) => setShowTitle(v);
  const onShowDate  = (v:boolean) => setShowDate(v);
  const onTitleFont = (e:React.ChangeEvent<HTMLSelectElement>) => setTitleFont(e.target.value);
  const onDateFont  = (e:React.ChangeEvent<HTMLSelectElement>) => setDateFont(e.target.value);
  const onTitleColor = (_e:React.ChangeEvent<HTMLInputElement>, v:string|number|File|Date) => setTitleColor(String(v));
  const onDateColor  = (_e:React.ChangeEvent<HTMLInputElement>, v:string|number|File|Date) => setDateColor(String(v));
  const onTimezone   = (e:React.ChangeEvent<HTMLSelectElement>) => setTimezone(e.target.value);
  const onTitleBold      = () => setTitleBold(v => !v);
  const onTitleUnderline = () => setTitleUnderline(v => !v);
  const onTitleItalic    = () => setTitleItalic(v => !v);
  const onDateBold       = () => setDateBold(v => !v);
  const onDateUnderline  = () => setDateUnderline(v => !v);
  const onDateItalic     = () => setDateItalic(v => !v);

  // ── setters "bruts" (valeur numérique directe) — partagés entre les champs du tableau et le drag&drop sur l'aperçu ──
  const setTitleXRaw    = (n:number) => setTitleX(n);
  const setTitleYRaw    = (n:number) => setTitleY(n);
  const setTitleSizeRaw = (n:number) => setTitleSize(n);
  const setDateXRaw     = (n:number) => setDateX(n);
  const setDateYRaw     = (n:number) => setDateY(n);
  const setDateSizeRaw  = (n:number) => setDateSize(n);

  const onTitleX    = (_e:React.ChangeEvent<HTMLInputElement>, v:string|number|File|Date) => setTitleXRaw(Number(v)||0);
  const onTitleY    = (_e:React.ChangeEvent<HTMLInputElement>, v:string|number|File|Date) => setTitleYRaw(Number(v)||0);
  const onTitleBoxOpacity = (_e:React.ChangeEvent<HTMLInputElement>, v:string|number|File|Date) => setTitleBoxOpacity(Math.max(0, Math.min(100, Math.round(Number(v)||0))));
  const onDateBoxOpacity  = (_e:React.ChangeEvent<HTMLInputElement>, v:string|number|File|Date) => setDateBoxOpacity(Math.max(0, Math.min(100, Math.round(Number(v)||0))));
  const onTitleSize = (_e:React.ChangeEvent<HTMLInputElement>, v:string|number|File|Date) => setTitleSizeRaw(Number(v)||0);
  const onDateX      = (_e:React.ChangeEvent<HTMLInputElement>, v:string|number|File|Date) => setDateXRaw(Number(v)||0);
  const onDateY      = (_e:React.ChangeEvent<HTMLInputElement>, v:string|number|File|Date) => setDateYRaw(Number(v)||0);
  const onDateSize   = (_e:React.ChangeEvent<HTMLInputElement>, v:string|number|File|Date) => setDateSizeRaw(Number(v)||0);

  const onTitleDrag   = (x:number, y:number) => { setTitleXRaw(x); setTitleYRaw(y); };
  const onDateDrag    = (x:number, y:number) => { setDateXRaw(x); setDateYRaw(y); };

  // ── Centrage des surimpressions Nom / Heure sur le canvas de référence ──
  // Choix du sens. Le repère de la mire change : nom et heure sont recentrés dans le nouveau
  // cadre, à une taille proportionnée à sa largeur (le portrait est 1080 de large, contre
  // 1920). À la création, la référence du compteur de modifications suit, pour que ce
  // recentrage ne compte pas comme une retouche ; en édition il compte (et sera enregistré).
  // Refusé tant que la chaîne tourne : il faut l'arrêter d'abord.
  const onOrientationChange = (value: string) => {
    if (channelRunning) return;
    const o: 'landscape' | 'portrait' = value === 'portrait' ? 'portrait' : 'landscape';
    if (o === orientation) return;
    const w = o === 'portrait' ? REFERENCE_HEIGHT : REFERENCE_WIDTH;
    const h = o === 'portrait' ? REFERENCE_WIDTH  : REFERENCE_HEIGHT;
    const k = w / REFERENCE_WIDTH;
    const tSize = Math.round((isEditMode ? titleSize : initialBC.titleSize * 4) * (isEditMode ? (w / refW) : k));
    const dSize = Math.round((isEditMode ? dateSize  : initialBC.dateSize  * 2) * (isEditMode ? (w / refW) : k));
    const tX = Math.round((w - measureTextWidth(name, titleFont, tSize)) / 2);
    const tY = Math.round((h - tSize) / 2);
    const dX = Math.round((w - measureTextWidth(previewTimeText, dateFont, dSize)) / 2);
    const dY = h - dSize - Math.round(80 * k);
    setOrientation(o);
    setTitleSize(tSize); setTitleX(tX); setTitleY(tY);
    setDateSize(dSize);  setDateX(dX);  setDateY(dY);
    if (!isEditMode) overlayRef.current = { ...overlayRef.current, titleX: tX, titleY: tY, titleSize: tSize, dateX: dX, dateY: dY, dateSize: dSize };
  };

  const onCenterTitleX = () => setTitleXRaw(Math.round((refW - measureTextWidth(name, titleFont, titleSize)) / 2));
  const onCenterTitleY = () => setTitleYRaw(Math.round((refH - titleSize) / 2));
  const onCenterDateX  = () => setDateXRaw(Math.round((refW - measureTextWidth(previewTimeText, dateFont, dateSize)) / 2));
  const onCenterDateY  = () => setDateYRaw(Math.round((refH - dateSize) / 2));

  // Positions rapides : marge de 5 % du cadre (la « zone sûre » des repères de l'aperçu).
  const placeTitleV = (where: 'top' | 'middle' | 'bottom') =>
    setTitleYRaw(where === 'top' ? Math.round(refH * 0.05) : where === 'middle' ? Math.round((refH - titleSize) / 2) : Math.round(refH * 0.95 - titleSize));
  const placeTitleH = (where: 'left' | 'center' | 'right') =>
    setTitleXRaw(where === 'left' ? Math.round(refW * 0.05) : where === 'center' ? Math.round((refW - measureTextWidth(name, titleFont, titleSize)) / 2) : Math.round(refW * 0.95 - measureTextWidth(name, titleFont, titleSize)));
  const placeDateV = (where: 'top' | 'middle' | 'bottom') =>
    setDateYRaw(where === 'top' ? Math.round(refH * 0.05) : where === 'middle' ? Math.round((refH - dateSize) / 2) : Math.round(refH * 0.95 - dateSize));
  const placeDateH = (where: 'left' | 'center' | 'right') =>
    setDateXRaw(where === 'left' ? Math.round(refW * 0.05) : where === 'center' ? Math.round((refW - measureTextWidth(previewTimeText, dateFont, dateSize)) / 2) : Math.round(refW * 0.95 - measureTextWidth(previewTimeText, dateFont, dateSize)));

  // Pendant la création (pas encore de position choisie par l'utilisateur), le nom saisi
  // reste centré horizontalement à chaque frappe — sa largeur change avec le texte.
  // En édition, on ne touche plus à la position une fois enregistrée.
  useEffect(() => {
    if (isEditMode) return;
    setTitleXRaw(Math.round((refW - measureTextWidth(name, titleFont, titleSize)) / 2));
  }, [name, titleFont, titleSize, isEditMode, refW]);
  const onStatus    = (e:React.ChangeEvent<HTMLSelectElement>) => setRequiredStatusId(parseInt(e.target.value));
  // Le nom du fichier tel que l'utilisateur l'a téléversé, montré à côté des icônes.
  // Un fichier fraîchement choisi prime sur celui de la fiche : c'est lui qui partira.
  // La source privée est effacée sitôt les copies publiques écrites (admin-api,
  // 19/08/2026) : ces colonnes sont tout ce qui reste du nom d'origine.
  const nomLogoOrigine     = logoUpload?.name    ?? initialBC.originalLogoImageFile    ?? '';
  const nomPendImgOrigine  = pendImgUpload?.name ?? initialBC.originalPendingImageFile ?? '';
  const nomPendSndOrigine  = pendSndUpload?.name ?? initialBC.originalPendingSoundFile ?? '';

  const onLogoChange    = (e:React.ChangeEvent<HTMLInputElement>) => { if(e?.target?.files) setLogoUpload(e.target.files[0]); };
  const onPendImgChange = (e:React.ChangeEvent<HTMLInputElement>) => { if(e?.target?.files) setPendImgUpload(e.target.files[0]); };
  const onPendSndChange = (e:React.ChangeEvent<HTMLInputElement>) => { if(e?.target?.files) setPendSndUpload(e.target.files[0]); };
  const onLogoDel       = () => setLogoDel(true);
  const onPendImgDel    = () => setPendImgDel(true);
  const onPendSndDel    = () => setPendSndDel(true);

  const onIpTvUser = (e:React.ChangeEvent<HTMLInputElement>) => setIpTvGenericalUser(e.target.value);
  const onIpTvPwd  = (e:React.ChangeEvent<HTMLInputElement>) => setIpTvGenericalPwd(e.target.value);

  const onCatToggle = (checked:boolean, cid:number) => {
    setCategoriesIds(prev => {
      const next=checked?[...prev,cid].filter((v,i,s)=>s.indexOf(v)===i):prev.filter(c=>c!==cid);
      setCatErr(next.length===0?'Au moins une catégorie requise':'');
      return next;
    });
  };

  const onTagCreate = async (kw:Keyword):Promise<number> => { const r=await createKeyword(kw.word); return r.code===1?r.payload:0; };
  const onTagChange = (tags:Keyword[]) => {
    setUsedKw(tags);
    setKeywordsIds(tags.map(k=>k.id));
  };

  // ── Handlers envoi mail ────────────────────────────────────────────────────
  const handlePublicUrlSending = () => setSendUrlModalOpen(true);

  // ── Save ───────────────────────────────────────────────────────────────────
const onSave = () => {
  if (nbMods === 0) return;
  const run = async () => {
    const en = await testName(name);
    const es = requiredStatusId <= 0 ? "Statut requis" : '';
    const ec = categoriesIds.length === 0 ? 'Catégorie requise' : '';
    setNameErr(en); setStatusErr(es); setCatErr(ec);
    if (en || es || ec) { setCheckErr(true); return; }

    // ← Utiliser broadcastingChannelToEdit directement (dernière valeur de la prop)
    //   plutôt que initialBC qui peut être périmé
    const baseBC = isEditMode
      ? broadcastingChannelToEdit!
      : initializedEnrichedBroadcastingChannel();

    onBroadcastingChannelChange?.(
      {
        ...baseBC,                          // ← garantit active, publicPortal, etc. à jour
        name,
        slug: generateSlug(name),
        description,
        logoImageFile: logoFile,
        pendingImageFile: pendImgFile,
        pendingSoundFile: pendSndFile,
        mode,
        orientation,
        categoriesIds,
        keywordsIds,
        chatModeration: chatModerationValue,
        stageOpen: stageAvailable && stageOpen,
        requiredUserStatusId: requiredStatusId,
        showTitle,
        showDate,
        titleFont,
        titleColor,
        titleX,
        titleY,
        titleSize,
        titleBoxOpacity,
        dateBoxOpacity,
        titleBold,
        titleUnderline,
        titleItalic,
        dateFont,
        dateColor,
        dateX,
        dateY,
        dateSize,
        dateBold,
        dateUnderline,
        dateItalic,
        timezone,
        ipTvGenericalUser,
        ipTvGenericalPwd,
      },
      logoUpload, pendImgUpload, pendSndUpload,
      logoDel, pendImgDel, pendSndDel,
      // parseInt tronquait les centimes (19,99 → 19) ; la virgule française est acceptée.
      isPaidStatus, parseFloat(soldPrice.replace(',', '.')) || 0, assocProduct,
      () => {}
    );
  };
  run();
};

  // ── Rendu catégories ───────────────────────────────────────────────────────
  const renderCats = () => {
    let title='Aucune catégorie'; let nb=0;
    const rows:JSX.Element[]=[];
    for (const c of bcCategories) {
      const chk=categoriesIds.includes(c.id);
      rows.push(<div key={`t-${c.id}`}><Toggle onChange={(s)=>onCatToggle(s,c.id)} checked={chk}/></div>);
      rows.push(<div key={`n-${c.id}`} className={styles.catName}>{c.name}</div>);
      if(chk){nb++;title=nb===1?c.name:nb===2?title+', '+c.name:nb===3?title+', ...':title;}
    }
    return (
      <div className={styles.catWrapper}>
        <div className={styles.catPanel}>
          <CollapsiblePanel title={title} overlayOnOpen={true} maxHeight="200px">
            <div className={styles.catGrid}>
              {rows}
            </div>
          </CollapsiblePanel>
        </div>
      </div>
    );
  };

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div className={styles.root}>

      {/* ── Fenêtres modales ── */}
      {checkErr && (
        <Window message={<>La fiche comporte des erreurs</>} messageType="information" onClickOnValidateButton={()=>setCheckErr(false)} validButtonText="Ok"/>
      )}

      {fileErr !== '' && (
        <Window message={<>{fileErr}</>} messageType="information" onClickOnValidateButton={()=>setFileErr('')} validButtonText="Ok"/>
      )}

      {catWindowOpen && (
        <Box width="1150px" maxHeight="700px"
          onClose={()=>{ getBroadcastingChannelCategories(authentication.admin?.id).then(r=>{if(r.code===1)setBcCategories(r.payload.sort((a,b)=>a.rank-b.rank));}); setCatWindowOpen(false); }}
          marginTop="0px">
          <BroadcastingChannelCategoryManager
            embedded={true}
            onCategoryCreated={(createdCategory) => {
              getBroadcastingChannelCategories(authentication.admin?.id).then(r => {
                if (r.code === 1) setBcCategories(r.payload.sort((a,b)=>a.rank-b.rank));
              });
              onCatToggle(true, createdCategory.id);
              setCatWindowOpen(false);
            }}
          />
        </Box>
      )}

      {/* ── Modale envoi URL publique ── */}
      {sendUrlModalOpen && (
        <SendChannelUrlModal
          channelName = {name}
          publicUrl   = {publicUrl}
          userId      = {authentication.admin ? authentication.admin.id : 0}
          onClose     = {() => setSendUrlModalOpen(false)}
        />
      )}

      {/* ── En-tête ── */}
      <div className={styles.configHead}>
        <span className={styles.headIcon}>
          <Icon name="gear" color="#4992FA" hoverable={false} clickable={false} width={20} height={20} />
        </span>
        <div className={styles.headTitleBlock}>
          <span className={styles.headTitle}>Configuration de la chaîne</span>
          <span className={styles.headSubtitle}>{subtitle}</span>
          <span className={styles.headPills}>
            <span className={styles.pill}>{orientation === 'portrait' ? 'Portrait 1080×1920' : 'Paysage 1920×1080'}</span>
          </span>
        </div>
        <div className={styles.headActions}>
          {nbMods>0 && <span className={styles.modBadge}>{nbMods} modification{nbMods>1?'s':''}</span>}
          <button className={classNames(styles.btn, styles.btnOutline)} onClick={creationMode && onCancel ? onCancel : init}>Annuler</button>
          <button
            className={classNames(styles.btn, nbMods > 0 ? styles.btnSave : styles.btnPrimary)}
            onClick={onSave}
          >
            <Ico.Save/>
            {isEditMode ? 'Enregistrer' : 'Créer la chaîne'}{nbMods > 0 && ` (${nbMods})`}
          </button>
        </div>
      </div>

      {/* ── Ligne principale : GÉNÉRAL (3/5) + MIRE D'ATTENTE (2/5) ── */}
      <div className={styles.mainRow}>

        {/* ── Colonne GAUCHE : Identité, Accès, Format, Public et interactions ── */}
        <div className={styles.leftCol}>

          {/* Sens du programme : modifiable chaîne ARRÊTÉE seulement (cf. onOrientationChange). */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Format du programme</div>
            <div className={styles.fmtRow}>
              <button type="button" disabled={channelRunning} onClick={() => onOrientationChange('landscape')}
                      className={classNames(styles.fmtCard, orientation === 'landscape' && styles.fmtCardOn)}>
                <span className={styles.fmtShape} style={{ width: 56, height: 32 }} />
                <span className={styles.fmtTxt}><b>Paysage</b>1920 × 1080 · écrans, TV, web</span>
              </button>
              <button type="button" disabled={channelRunning} onClick={() => onOrientationChange('portrait')}
                      className={classNames(styles.fmtCard, orientation === 'portrait' && styles.fmtCardOn)}>
                <span className={styles.fmtShape} style={{ width: 26, height: 46 }} />
                <span className={styles.fmtTxt}><b>Portrait</b>1080 × 1920 · smartphone vertical</span>
              </button>
            </div>
            <div className={styles.fmtHint}>
              {channelRunning
                ? "Arrêtez la chaîne pour changer le format du programme."
                : "Portrait : tout le programme est en 1080×1920. Le changement prend effet à la prochaine activation."}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Identité</div>
            <div className={styles.identRow}>
              <div className={styles.identMain}>
            <div className={styles.field}>
              <label className={styles.lbl}>Nom</label>
              <Field type="text" value={name} onChange={onName}/>
              {nameErr && <span className={styles.err}>{nameErr}</span>}
            </div>

            <div className={styles.fieldGrow}>
              <label className={styles.lbl}>Description</label>
              <TextArea value={description} onChange={onDesc}/>
            </div>

              </div>
              <div className={styles.identLogo}>
            <div className={styles.fieldMiniature}>
              <label className={styles.lbl}>Logo de la chaîne</label>
              <FieldMedia placeholder="" value={logoSrc} onDelete={onLogoDel} onChange={onLogoChange} forceType="Image" onFileRejected={(f)=>setFileErr(`« ${f.name} » n'est pas une image : le logo de la chaîne doit être une image.`)} showTextInput={false} previewHeight="140px" previewWidth="140px" showPreview upload={true} previewAlwaysShown previewClickable previewPosition="left_top" iconSquareColor="var(--color-field-icon)" fileName={nomLogoOrigine}/>
            </div>

              </div>
            </div>
            <div className={styles.twoCols}>
            <div className={styles.field}>
              <label className={styles.lbl}>Catégories</label>
              {renderCats()}
              {catErr && <span className={styles.err}>{catErr}</span>}
            </div>

            <div className={styles.fieldGrow}>
              <label className={styles.lbl}>Mots-clés</label>
              <TagInput availableTags={kwDict} initialTags={usedKw} onTagListChange={onTagChange} onTagCreate={onTagCreate}/>
            </div>

            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Accès</div>
            <div className={styles.toggleRow}>
              <div className={styles.field} style={{flex:2}}>
                <label className={styles.lbl}>Autorisation</label>
                <Select choices={userStatuses.map(s=>`${s.id}`)} choicesLabel={userStatuses.map(s=>s.name)} value={`${requiredStatusId}`} onChange={onStatus} showPlaceholder={false}/>
                {statusErr && <span className={styles.err}>{statusErr}</span>}
              </div>
              {isPaidStatus && (
                <div className={styles.field} style={{flex:1}}>
                  <label className={styles.lbl}>Prix TTC (€)</label>
                  <Field type="text" value={soldPrice} onChange={e=>setSoldPrice(e.target.value)}/>
                </div>
              )}
            </div>

          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Public et interactions</div>

            <div className={styles.settingRow}>
              <div className={styles.settingTxt}>
                <b>Chat</b>
                <span>Les spectateurs peuvent écrire. Le chat ouvert est toujours trié par l'IA.</span>
              </div>
              {/* Le chat ouvert est toujours trié par l'IA : reste à dire jusqu'où. Même
                  réglage que l'écran Modération, qui le change aussi. */}
              {chatAllowed && (
                <div className={styles.settingCtl}>
                  <label className={styles.lbl}>Niveau de modération</label>
                  <Select choices={chatModerationChoices} choicesLabel={chatModerationLabels} value={chatModerationLevel} onChange={e=>setChatModerationLevel(e.target.value)} showPlaceholder={false}/>
                  <span className={styles.settingHint}>
                    {CHAT_MODERATION_LEVELS.find(l => String(l.value) === chatModerationLevel)?.hint}
                  </span>
                </div>
              )}
              <Toggle checked={chatAllowed} form="switch" onChange={setChatAllowed}/>
            </div>

            {/* Les spectateurs connectés de Kleon.tv demandent à devenir caméra invitée ;
                le réalisateur accepte ou refuse dans Sources distantes › Public. */}
            {stageAvailable && (
              <div className={styles.settingRow}>
                <div className={styles.settingTxt}>
                  <b>Ouvrir le plateau au public</b>
                  <span>
                    {stageOpen
                      ? "Le bouton « Monter sur le plateau » paraît sur Kleon.tv pendant le direct. Vous acceptez chaque demande, l'invité arrive en coulisses."
                      : "Les spectateurs ne peuvent pas demander à rejoindre le direct."}
                  </span>
                </div>
                <Toggle checked={stageOpen} form="switch" onChange={setStageOpen}/>
              </div>
            )}
          </div>

        </div>

        {/* ── Bloc MIRE D'ATTENTE ── */}
        <div className={classNames(styles.section, styles.sectionMire)}>
          <div className={styles.sectionTitle}>Mire d'attente <span className={styles.titleNote}>{refW} × {refH}</span></div>
          <div className={styles.mireStack}>

            {/* Image d'attente */}
            <div className={styles.fieldPendingMedia}>
              <label className={styles.lbl}>Image d'attente</label>
              <FieldMediaPendingImage
                placeholder=""
                value={pendImgSrc}
                onDelete={onPendImgDel}
                onChange={onPendImgChange}
                onFileRejected={(f)=>setFileErr(`« ${f.name} » n'est pas une image : l'image d'attente doit être une image.`)}
                showTextInput={false}
                layout="stacked"
                previewWidth={orientation === 'portrait' ? MIRE_PREVIEW_WIDTH_PORTRAIT : MIRE_PREVIEW_WIDTH}
                referenceWidth={refW}
                referenceHeight={refH}
                showPreview
                upload={true}
                previewAlwaysShown
                previewClickable={false}
                previewPosition="left_top"
                iconSquareColor="var(--color-field-icon)"
                defaultImage={defaultPendingImage}
                fileName={nomPendImgOrigine}
                title={{ text: name, visible: showTitle, font: titleFont, color: titleColor, x: titleX, y: titleY, size: titleSize, boxOpacity: titleBoxOpacity, bold: titleBold, underline: titleUnderline, italic: titleItalic, onMove: onTitleDrag, onResize: setTitleSizeRaw }}
                date={{ text: previewTimeText, visible: showDate, font: dateFont, color: dateColor, x: dateX, y: dateY, size: dateSize, boxOpacity: dateBoxOpacity, bold: dateBold, underline: dateUnderline, italic: dateItalic, onMove: onDateDrag, onResize: setDateSizeRaw }}
                onTitleVisibleChange={onShowTitle}
                onDateVisibleChange={onShowDate}
                selectedOverlay={selectedOverlay}
                onSelectedOverlayChange={setSelectedOverlay}
              />
            </div>

            {/* Colonne droite : réglages de l'élément sélectionné (au plus un) sur l'aperçu.
                Un clic dans cette colonne ne désélectionne pas (cf. useEffect handleOutsideMouseDown) */}
            <div className={styles.mireRight} ref={mireRightRef}>
              {(showTitle || showDate) && (
                <div className={styles.overlayTabs} role="tablist">
                  <button type="button" role="tab" disabled={!showTitle} aria-selected={overlayAffiche === 'title'}
                          className={classNames(styles.overlayTab, overlayAffiche === 'title' && styles.overlayTabOn)}
                          onClick={() => setSelectedOverlay('title')}>Nom</button>
                  <button type="button" role="tab" disabled={!showDate} aria-selected={overlayAffiche === 'date'}
                          className={classNames(styles.overlayTab, overlayAffiche === 'date' && styles.overlayTabOn)}
                          onClick={() => setSelectedOverlay('date')}>Heure</button>
                </div>
              )}
              {!overlayAffiche && (
                <div className={styles.overlayHint}>
                  Activez « Nom » et/ou « Date » avec leurs interrupteurs, puis sélectionnez
                  l'élément sur l'aperçu pour régler sa police, sa couleur, sa position et sa taille.
                </div>
              )}

              {overlayAffiche === 'title' && (
                <div className={styles.overlayTable}>
                  <div className={styles.overlayTableHeadCell}>Nom</div>

                  <div className={styles.overlayTableRowLabel}>Police</div>
                  <div className={styles.overlayTableCell}>
                    <Select choices={mireFontChoices} value={titleFont} onChange={onTitleFont} showPlaceholder={false} width="100%"/>
                  </div>

                  <div className={styles.overlayTableRowLabel}>Style</div>
                  <div className={styles.overlayTableCell}>
                    <div className={styles.styleBtnGroup}>
                      <div className={classNames(styles.styleBtn, titleBold && styles.styleBtnActive)} onClick={onTitleBold} title="Gras"><span style={{fontWeight: 700}}>G</span></div>
                      <div className={classNames(styles.styleBtn, titleUnderline && styles.styleBtnActive)} onClick={onTitleUnderline} title="Souligné"><span style={{textDecoration: 'underline'}}>S</span></div>
                      <div className={classNames(styles.styleBtn, titleItalic && styles.styleBtnActive)} onClick={onTitleItalic} title="Italique"><span style={{fontStyle: 'italic'}}>I</span></div>
                    </div>
                  </div>

                  <div className={styles.overlayTableRowLabel}>Couleur</div>
                  <div className={styles.overlayTableCell}>
                    <FieldColor value={titleColor} onChange={onTitleColor} showField={false} width="fit-content"/>
                  </div>

                  <div className={styles.overlayTableRowLabel}>x</div>
                  <div className={styles.overlayTableCell}>
                    <div className={styles.xyField}>
                      <Field type="number" decimalPlaces={0} value={titleX} onChange={onTitleX}/>
                      <div className={styles.squareIconBtn} onClick={onCenterTitleX} title="Centrer horizontalement">
                        <Ico.AlignH/>
                      </div>
                    </div>
                  </div>

                  <div className={styles.overlayTableRowLabel}>y</div>
                  <div className={styles.overlayTableCell}>
                    <div className={styles.xyField}>
                      <Field type="number" decimalPlaces={0} value={titleY} onChange={onTitleY}/>
                      <div className={styles.squareIconBtn} onClick={onCenterTitleY} title="Centrer verticalement">
                        <Ico.AlignV/>
                      </div>
                    </div>
                  </div>

                  <div className={styles.overlayTableRowLabel}>Placer</div>
                  <div className={styles.overlayTableCell}>
                    <div className={styles.placeRow}>
                      <div className={styles.placeGroup}>
                        <button type="button" className={styles.placeBtn} onClick={() => placeTitleV('top')}>Haut</button>
                        <button type="button" className={styles.placeBtn} onClick={() => placeTitleV('middle')}>Milieu</button>
                        <button type="button" className={styles.placeBtn} onClick={() => placeTitleV('bottom')}>Bas</button>
                      </div>
                      <div className={styles.placeGroup}>
                        <button type="button" className={styles.placeBtn} onClick={() => placeTitleH('left')}>Gauche</button>
                        <button type="button" className={styles.placeBtn} onClick={() => placeTitleH('center')}>Centre</button>
                        <button type="button" className={styles.placeBtn} onClick={() => placeTitleH('right')}>Droite</button>
                      </div>
                    </div>
                  </div>

                  <div className={styles.overlayTableRowLabel}>Taille</div>
                  <div className={styles.overlayTableCell}>
                    <div className={styles.rangeRow}><RangeInput label="Taille" min={8} max={600} value={titleSize} onChange={v => setTitleSizeRaw(v)}/><Field type="number" decimalPlaces={0} value={titleSize} onChange={onTitleSize}/></div>
                  </div>

                  <div className={styles.overlayTableRowLabel}>Fond (%)</div>
                  <div className={styles.overlayTableCell}>
                    <div className={styles.rangeRow}><RangeInput label="Fond" min={0} max={100} value={titleBoxOpacity} onChange={v => setTitleBoxOpacity(v)}/><Field type="number" decimalPlaces={0} value={titleBoxOpacity} onChange={onTitleBoxOpacity}/></div>
                  </div>
                </div>
              )}

              {overlayAffiche === 'date' && (
                <div className={styles.overlayTable}>
                  <div className={styles.overlayTableHeadCell}>Heure</div>

                  <div className={styles.overlayTableRowLabel}>Police</div>
                  <div className={styles.overlayTableCell}>
                    <Select choices={mireFontChoices} value={dateFont} onChange={onDateFont} showPlaceholder={false} width="100%"/>
                  </div>

                  <div className={styles.overlayTableRowLabel}>Style</div>
                  <div className={styles.overlayTableCell}>
                    <div className={styles.styleBtnGroup}>
                      <div className={classNames(styles.styleBtn, dateBold && styles.styleBtnActive)} onClick={onDateBold} title="Gras"><span style={{fontWeight: 700}}>G</span></div>
                      <div className={classNames(styles.styleBtn, dateUnderline && styles.styleBtnActive)} onClick={onDateUnderline} title="Souligné"><span style={{textDecoration: 'underline'}}>S</span></div>
                      <div className={classNames(styles.styleBtn, dateItalic && styles.styleBtnActive)} onClick={onDateItalic} title="Italique"><span style={{fontStyle: 'italic'}}>I</span></div>
                    </div>
                  </div>

                  <div className={styles.overlayTableRowLabel}>Fuseau</div>
                  <div className={styles.overlayTableCell}>
                    <Select choices={TIMEZONE_CHOICES} choicesLabel={TIMEZONE_LABELS} value={timezone} onChange={onTimezone} showPlaceholder={false} width="100%"/>
                  </div>

                  <div className={styles.overlayTableRowLabel}>Couleur</div>
                  <div className={styles.overlayTableCell}>
                    <FieldColor value={dateColor} onChange={onDateColor} showField={false} width="fit-content"/>
                  </div>

                  <div className={styles.overlayTableRowLabel}>x</div>
                  <div className={styles.overlayTableCell}>
                    <div className={styles.xyField}>
                      <Field type="number" decimalPlaces={0} value={dateX} onChange={onDateX}/>
                      <div className={styles.squareIconBtn} onClick={onCenterDateX} title="Centrer horizontalement">
                        <Ico.AlignH/>
                      </div>
                    </div>
                  </div>

                  <div className={styles.overlayTableRowLabel}>y</div>
                  <div className={styles.overlayTableCell}>
                    <div className={styles.xyField}>
                      <Field type="number" decimalPlaces={0} value={dateY} onChange={onDateY}/>
                      <div className={styles.squareIconBtn} onClick={onCenterDateY} title="Centrer verticalement">
                        <Ico.AlignV/>
                      </div>
                    </div>
                  </div>

                  <div className={styles.overlayTableRowLabel}>Placer</div>
                  <div className={styles.overlayTableCell}>
                    <div className={styles.placeRow}>
                      <div className={styles.placeGroup}>
                        <button type="button" className={styles.placeBtn} onClick={() => placeDateV('top')}>Haut</button>
                        <button type="button" className={styles.placeBtn} onClick={() => placeDateV('middle')}>Milieu</button>
                        <button type="button" className={styles.placeBtn} onClick={() => placeDateV('bottom')}>Bas</button>
                      </div>
                      <div className={styles.placeGroup}>
                        <button type="button" className={styles.placeBtn} onClick={() => placeDateH('left')}>Gauche</button>
                        <button type="button" className={styles.placeBtn} onClick={() => placeDateH('center')}>Centre</button>
                        <button type="button" className={styles.placeBtn} onClick={() => placeDateH('right')}>Droite</button>
                      </div>
                    </div>
                  </div>

                  <div className={styles.overlayTableRowLabel}>Taille</div>
                  <div className={styles.overlayTableCell}>
                    <div className={styles.rangeRow}><RangeInput label="Taille" min={8} max={600} value={dateSize} onChange={v => setDateSizeRaw(v)}/><Field type="number" decimalPlaces={0} value={dateSize} onChange={onDateSize}/></div>
                  </div>

                  <div className={styles.overlayTableRowLabel}>Fond (%)</div>
                  <div className={styles.overlayTableCell}>
                    <div className={styles.rangeRow}><RangeInput label="Fond" min={0} max={100} value={dateBoxOpacity} onChange={v => setDateBoxOpacity(v)}/><Field type="number" decimalPlaces={0} value={dateBoxOpacity} onChange={onDateBoxOpacity}/></div>
                  </div>
                </div>
              )}
            </div>

              <div className={styles.field}>
                <label className={styles.lbl}>
                  Son d'attente
                  {pendSndSending && (
                    <span style={{ marginLeft: '10px', fontWeight: 400, color: 'var(--color-field-icon)' }}>
                      envoi en cours… {(pendSndUploadState?.progress ?? 0).toFixed(0)} %
                    </span>
                  )}
                  {pendSndSendFailed && (
                    <span style={{ marginLeft: '10px', fontWeight: 400, color: '#ef4444' }}>
                      l'envoi a échoué
                    </span>
                  )}
                </label>
                {/* Pas de zone de texte ici : le nom du fichier sur le serveur
                    (`B000001_…_pending.mp3`) n'apprend rien, et il n'est pas modifiable
                    à la main. Ne restent que le lecteur et ses commandes à droite. */}
                <FieldSound placeholder="" width="100%" value={pendSndSrc} onDelete={onPendSndDel} onChange={onPendSndChange} onFileRejected={(f)=>setFileErr(`« ${f.name} » n'est pas un son : le son d'attente doit être un fichier audio.`)}  iconSquareColor="var(--color-field-icon)" showTextInput={false} previewWidth={PENDING_PREVIEW_WIDTH} fileName={nomPendSndOrigine}/>
              </div>

          </div>
        </div>

      </div>{/* fin .mainRow */}

      {/* ── Blocs FLUX : 3 panneaux côte à côte ── */}
      <div className={styles.fluxRow}>

        {/* Panneau 1 : Réception flux externe */}
        <div className={styles.fluxPanel}>
          <div className={styles.fluxPanelTitle}>Réception flux externe (OBS, RTMP...)</div>
          <div style={{display:'flex', gap:8}}>
            <div className={styles.field} style={{flex:1, minWidth:0}}><label className={styles.lbl}>RTMP</label><Field type="text" readOnly value={rtmpUrl} copyable iconSquareColor="var(--color-field-icon)"/></div>
            <div className={styles.field} style={{flex:1, minWidth:0}}><label className={styles.lbl}>RTMPS</label><Field type="text" readOnly value={getIngressUrlRtmps()} copyable iconSquareColor="var(--color-field-icon)"/></div>
          </div>
          <div className={styles.field}>
            <label className={styles.lbl}>Clé</label>
            <div style={{display:'flex', gap:8, alignItems:'center'}}>
              <div style={{flex:1}}><Field type="text" readOnly value={streamKey} copyable iconSquareColor="var(--color-field-icon)"/></div>
              {isEditMode && (
                <button type="button" onClick={handleRegenerateRtmpKey} disabled={regenBusy}
                        title="Régénérer la clé de stream" aria-label="Régénérer la clé de stream"
                        style={{background:'none', border:'none', padding:4, cursor: regenBusy ? 'wait' : 'pointer', opacity: regenBusy ? 0.5 : 1, display:'flex'}}>
                  <Icon name="RefreshCw" color="var(--color-Icon-FG)" hoverColor="var(--color-Icon-hover-FG)" width={20} height={20} clickable={false} />
                </button>
              )}
            </div>
            {regenError && <div style={{color:'#e5484d', fontSize:12, marginTop:4}}>{regenError}</div>}
          </div>
        </div>

        {/* Panneau 2 : URL de diffusion */}
        <div className={styles.fluxPanel}>
          <div className={styles.fluxPanelTitle}>URL de diffusion de la chaîne</div>
          <div className={styles.field}><label className={styles.lbl}>RTSP</label><Field type="text" readOnly value={rtspUrl} copyable iconSquareColor="var(--color-field-icon)"/></div>
          <div className={styles.field}>
            <label className={styles.lbl}>Url publique</label>
            <div style={{display:'flex', gap:8, alignItems:'center'}}>
              <div style={{flex:1}}><Field type="text" readOnly value={publicUrl} copyable mailSendable ={true} onMailSend={handlePublicUrlSending} iconSquareColor="var(--color-field-icon)"/></div>
            </div>
          </div>
        </div>

        {/* Panneau 3 : Accès IPTV */}
        <div className={styles.fluxPanel}>
          <div className={styles.fluxPanelTitle}>Accès IPTV</div>
          <div className={styles.field}><label className={styles.lbl}>Url du serveur</label><Field type="text" readOnly value={ipTvUrl} copyable iconSquareColor="var(--color-field-icon)"/></div>
          <div className={styles.fluxPanelRow}>
            <div className={styles.field}>
              <label className={styles.lbl}>Identifiant</label>
              <Field type="text" value={ipTvGenericalUser} onChange={onIpTvUser} copyable iconSquareColor="var(--color-field-icon)"/>
            </div>
            <div className={styles.field}>
              <label className={styles.lbl}>Mot de passe</label>
              <Field type="text" value={ipTvGenericalPwd} onChange={onIpTvPwd} copyable iconSquareColor="var(--color-field-icon)"/>
            </div>
          </div>
        </div>

      </div>

      {/* ── Annonce « Je suis en direct » sur Facebook et Instagram (2026-10-17) ──
          Section autonome : elle enregistre elle-même son réglage, hors du bouton
          Enregistrer de la fiche. Il faut une chaîne qui existe déjà. */}
      {isEditMode && broadcastingChannelToEdit && broadcastingChannelToEdit.id > 0 && (
        <LiveAnnounceSection adminId={authentication.admin?.id} channelId={broadcastingChannelToEdit.id}/>
      )}

    </div>
  );
};

export default BroadcastingChannelConfig;